import type { VideoSource } from '@src/stores-v2/types';

export type TimelineAssetRef = {
  url: string;
  /** Element start time in seconds, if known. Used for playhead-proximity sort. */
  time?: number;
};

const CACHEABLE_TYPES = new Set(['video', 'image', 'audio']);

/**
 * Walk a Creatomate VideoSource JSON and return every video/image/audio
 * asset URL the timeline references (including nested composition elements).
 * De-duplicated by URL, insertion order. Skips elements without a `source`.
 *
 * Times inside composition elements are relative to the composition's own
 * time, so we accumulate the parent's absolute time as we recurse. Elements
 * whose own `time` is missing inherit the composition's absolute time
 * (matching Creatomate's "starts when its container starts" default).
 * Top-level elements without an explicit time stay `undefined` so the
 * proximity sort de-prioritizes them.
 */
export function extractAllAssetRefs(
  videoSource: VideoSource | undefined,
): TimelineAssetRef[] {
  if (!videoSource?.elements?.length) return [];

  const seen = new Set<string>();
  const refs: TimelineAssetRef[] = [];

  const walk = (
    elements: Array<Record<string, any>>,
    parentAbsoluteTime: number | undefined,
  ) => {
    for (const el of elements) {
      if (!el || typeof el !== 'object') continue;

      const ownTime = typeof el.time === 'number' ? el.time : undefined;
      let absoluteTime: number | undefined;
      if (ownTime !== undefined) {
        absoluteTime = (parentAbsoluteTime ?? 0) + ownTime;
      } else if (parentAbsoluteTime !== undefined) {
        // Element has no time but lives inside a placed composition —
        // inherit the composition's absolute time as its approximate
        // position (relative offset 0).
        absoluteTime = parentAbsoluteTime;
      } else {
        absoluteTime = undefined;
      }

      if (
        typeof el.type === 'string' &&
        CACHEABLE_TYPES.has(el.type) &&
        typeof el.source === 'string' &&
        el.source.length > 0 &&
        !seen.has(el.source)
      ) {
        seen.add(el.source);
        refs.push({ url: el.source, time: absoluteTime });
      }

      if (Array.isArray(el.elements)) {
        walk(el.elements, absoluteTime);
      }
    }
  };

  walk(videoSource.elements, undefined);
  return refs;
}

/**
 * Sort refs by absolute time distance from the playhead.
 * Refs without a `time` field land at the end (lowest priority).
 * Stable for ties.
 */
export function sortByPlayheadProximity(
  refs: TimelineAssetRef[],
  playheadSec: number,
): TimelineAssetRef[] {
  return refs
    .map((ref, index) => ({
      ref,
      index,
      distance:
        ref.time === undefined
          ? Number.POSITIVE_INFINITY
          : Math.abs(ref.time - playheadSec),
    }))
    .sort((a, b) => {
      if (a.distance !== b.distance) return a.distance - b.distance;
      return a.index - b.index;
    })
    .map(entry => entry.ref);
}
